import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  inject,
} from '@angular/core';

/** Controls an editor writes with; a read-only action opts out with `data-locked-allowed`. */
const EDIT_CONTROLS = [
  'button',
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  'mat-select',
  'mat-checkbox',
  'mat-slide-toggle',
  'mat-radio-button',
  'mat-datepicker-toggle',
  'mat-chip-grid',
  '.cdk-drag',
].join(',');

/** Navigation stays usable: tabs, their scroll arrows. */
const NAVIGATION = '.mat-mdc-tab-header';

/**
 * Makes everything inside read-only while `pulpoLocked` is true, without `inert`: the content can
 * still be scrolled, selected and copied, tooltips show, and the buttons marked
 * `data-locked-allowed` (consulting or exporting prices) keep working. Typing is refused by
 * read-only fields, clicks and keys on edit controls are swallowed before they reach them.
 * Content rendered later (a tab opened, a list loaded) is covered too.
 */
@Directive({
  selector: '[pulpoLocked]',
  standalone: true,
})
export class PulpoLockedDirective implements AfterViewInit, OnDestroy {
  private locked = false;
  private observer?: MutationObserver;
  // The fields made read-only here, given back on unlock: the page may stay while the lock lifts
  private readonly lockedFields = new Set<HTMLInputElement | HTMLTextAreaElement>();

  @HostBinding('class.pulpo-locked')
  get lockedClass(): boolean {
    return this.locked;
  }

  @Input()
  set pulpoLocked(locked: boolean | null | undefined) {
    this.locked = !!locked;
    this.lockFields();
  }

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  ngAfterViewInit(): void {
    const element = this.host.nativeElement;
    this.zone.runOutsideAngular(() => {
      element.addEventListener('click', this.swallow, true);
      element.addEventListener('mousedown', this.swallow, true);
      element.addEventListener('keydown', this.swallowKeys, true);
      this.observer = new MutationObserver(() => this.lockFields());
      this.observer.observe(element, { childList: true, subtree: true });
    });
    this.lockFields();
  }

  ngOnDestroy(): void {
    const element = this.host.nativeElement;
    element.removeEventListener('click', this.swallow, true);
    element.removeEventListener('mousedown', this.swallow, true);
    element.removeEventListener('keydown', this.swallowKeys, true);
    this.observer?.disconnect();
  }

  /** Text fields stay focusable and copyable but refuse typing. */
  private lockFields(): void {
    if (!this.locked) {
      this.lockedFields.forEach((field) => (field.readOnly = false));
      this.lockedFields.clear();
      return;
    }
    this.host.nativeElement
      .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
      .forEach((field) => {
        if (!this.allowed(field) && !field.readOnly) {
          field.readOnly = true;
          this.lockedFields.add(field);
        }
      });
  }

  private readonly swallow = (event: Event): void => {
    const control = this.editControl(event.target);
    // A read-only text field may be clicked into, to select or copy its text.
    if (control && !this.isTextField(control)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  private readonly swallowKeys = (event: KeyboardEvent): void => {
    const control = this.editControl(event.target);
    if (!control) {
      return;
    }
    const navigationKey = ['Tab', 'Escape'].includes(event.key) || event.ctrlKey || event.metaKey;
    if (!navigationKey) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  private editControl(target: EventTarget | null): Element | null {
    if (!this.locked || !(target instanceof Element)) {
      return null;
    }
    const control = target.closest(EDIT_CONTROLS);
    if (!control || !this.host.nativeElement.contains(control) || this.allowed(control)) {
      return null;
    }
    return control;
  }

  private allowed(element: Element): boolean {
    return !!element.closest(`[data-locked-allowed], ${NAVIGATION}`);
  }

  private isTextField(element: Element): boolean {
    return (
      (element instanceof HTMLInputElement &&
        !['checkbox', 'radio', 'button', 'submit', 'file'].includes(element.type)) ||
      element instanceof HTMLTextAreaElement
    );
  }
}
